/**
 * Seal Subscriptions – Customer Portal Rewards Augmentation
 * Route: /a/subscriptions/manage (app proxy, rendered inside layout/theme.liquid)
 *
 * The Seal portal is SERVER-rendered, so the subscription DOM already exists at
 * DOMContentLoaded. This module:
 *   1. Tags the page PC/PPC (CSS hides the discount field for PPC).
 *   2. Hides Seal's raw internal line-item properties ( _portal_id, _bv, … ).
 *   3. Renders the empty-state message on the list page when there are none.
 *   4. Augments each subscription line item with reward pills (Cashback for all;
 *      UC / BV / IBV for PPC) + the SGP BCRS note for Ultimate Aloe.
 *
 * Line items expose only a Shopify product id (data-item-product-id="s<ID>") and
 * a variant name string. Rewards are resolved by product id via the Storefront
 * Search + Section Rendering API:
 *   /search?q=id:PID1 OR id:PID2&type=product&section_id=variant-rewards-data
 * then each line item is matched to a variant by name.
 *
 * Config is read from #seal-portal-config (see snippets/seal-portal-assets.liquid).
 * Every step degrades gracefully — failures never break the portal UI.
 */

const SECTION_ID = 'variant-rewards-data';
const PAGE_SELECTOR = '.seal-subscription-page';
const ITEM_SELECTOR = '[data-item-product-id]';
const ITEMS_LIST_SELECTOR = '#seal-subscription-items-list';
const LIST_SELECTOR = '.seal-subscriptions-list';
const VARIANT_NAME_SELECTOR = '.seal-product-variant-name';
const QUANTITY_INPUT_SELECTOR = 'input[data-seal-quantity]';
const MAX_SUBSCRIPTION_QUANTITY = 50;

/** Configure Seal quantity fields for integer-only entry and mobile keyboards. */
function configureQuantityInputs(page) {
  for (const input of page.querySelectorAll(QUANTITY_INPUT_SELECTOR)) {
    input.type = 'text';
    input.inputMode = 'numeric';
    input.pattern = '[0-9]*';
    input.max = String(MAX_SUBSCRIPTION_QUANTITY);
  }
}

/**
 * Keep Seal quantity editing stable. Native focus can scroll the app-proxy page
 * when the field is near a viewport edge, so focus it without scrolling. Seal
 * accepts number syntax such as decimals/exponents even though subscription
 * quantities must be positive whole numbers; block and normalise those values
 * before Seal receives them.
 */
function bindQuantityInputGuards(page) {
  let lockedScrollY = null;
  let releasingScrollLock = 0;
  let correctingScroll = false;

  const isQuantityInput = (target) =>
    target instanceof HTMLInputElement && target.matches(QUANTITY_INPUT_SELECTOR);

  const keepScrollLocked = () => {
    if (lockedScrollY === null || correctingScroll || !isQuantityInput(document.activeElement)) return;
    if (Math.abs(window.scrollY - lockedScrollY) < 1) return;
    correctingScroll = true;
    window.scrollTo({ top: lockedScrollY, left: window.scrollX, behavior: 'instant' });
    correctingScroll = false;
  };

  const releaseScrollLockSoon = () => {
    window.clearTimeout(releasingScrollLock);
    releasingScrollLock = window.setTimeout(() => {
      keepScrollLocked();
      lockedScrollY = null;
    }, 250);
  };

  window.addEventListener('scroll', keepScrollLocked, { passive: true });

  page.addEventListener('keydown', (event) => {
    if (!isQuantityInput(event.target)) return;
    window.clearTimeout(releasingScrollLock);
    lockedScrollY = window.scrollY;
  }, true);

  page.addEventListener('keyup', (event) => {
    if (!isQuantityInput(event.target)) return;
    keepScrollLocked();
    releaseScrollLockSoon();
  }, true);

  page.addEventListener('pointerdown', (event) => {
    const input = event.target instanceof Element ? event.target.closest(QUANTITY_INPUT_SELECTOR) : null;
    if (!(input instanceof HTMLInputElement) || event.button !== 0) return;
    lockedScrollY = window.scrollY;
  }, true);

  page.addEventListener('pointerup', (event) => {
    const input = event.target instanceof Element ? event.target.closest(QUANTITY_INPUT_SELECTOR) : null;
    if (!(input instanceof HTMLInputElement) || event.button !== 0) return;
    releaseScrollLockSoon();
  }, true);

  page.addEventListener('beforeinput', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches(QUANTITY_INPUT_SELECTOR)) return;
    if (event.inputType.startsWith('insert') && event.data && !/^\d+$/.test(event.data)) {
      event.preventDefault();
    }
  });

  page.addEventListener('input', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches(QUANTITY_INPUT_SELECTOR)) return;

    const caret = input.selectionStart;
    const oldValue = input.value;

    const digits = oldValue.replace(/\D/g, '');
    let newValue = digits;
    if (digits !== '') {
      const min = Number.parseInt(input.min, 10) || 1;
      const configuredMax = Number.parseInt(input.max, 10);
      const value = Math.max(min, Number.parseInt(digits, 10));
      newValue = Number.isFinite(configuredMax) ? String(Math.min(value, configuredMax)) : String(value);
    }

    if (newValue !== oldValue) {
      input.value = newValue;
      const newCaret = Math.min(Math.max(0, caret - (oldValue.length - newValue.length)), newValue.length);
      input.setSelectionRange(newCaret, newCaret);
    }

    keepScrollLocked();
    releaseScrollLockSoon();
  });

  page.addEventListener('blur', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches(QUANTITY_INPUT_SELECTOR)) return;
    const min = Number.parseInt(input.min, 10) || 1;
    const value = Number.parseInt(input.value, 10);
    if (!/^\d+$/.test(input.value) || value < min) {
      input.value = String(min);
    } else if (value > MAX_SUBSCRIPTION_QUANTITY) {
      input.value = String(MAX_SUBSCRIPTION_QUANTITY);
    }
    lockedScrollY = null;
  }, true);
}

/** @returns {object|null} parsed config from the inline JSON block */
function getConfig() {
  const el = document.getElementById('seal-portal-config');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

/**
 * Read the site-wide customer reward decisions computed by meta-tags.liquid.
 * Defaults match a plain retail customer when the meta tag is unavailable.
 */
function getCustomerContext() {
  const meta = document.querySelector('meta[name="ma:customer"]');
  const data = (meta instanceof HTMLElement && meta.dataset) || {};
  const bool = (value) => value === 'true';
  return {
    isPpc: bool(data.isPpc),
    isMpa: bool(data.isMpa),
    isIbvOnly: bool(data.isIbvOnly),
    isUsOrCanada: bool(data.isUsOrCanada),
    isDistributorDisplay: bool(data.isDistributorDisplay),
    isBvIbvCustomer: bool(data.isBvIbvCustomer),
    canShowIbv: bool(data.canShowIbv),
  };
}

/** Normalise a product/variant name for fuzzy matching. */
function normalizeName(str) {
  return (str || '')
    .toLowerCase()
    .replace(/[®™©]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b[uw]\b\s*$/, '') // drop trailing distributor suffix ("-U" / "-W")
    .trim();
}

/** Extract the numeric Shopify product id from a Seal line item. */
function productIdFromItem(item) {
  const raw = item.getAttribute('data-item-product-id') || '';
  const match = raw.match(/(\d{6,})/); // Seal prefixes with "s" → grab the digits
  return match ? match[1] : null;
}

/** The visible variant name text for a line item. */
function itemVariantName(item) {
  const el = item.querySelector('.seal-product-variant-name');
  return el ? el.textContent.trim() : '';
}

/** Format a money amount using the configured currency symbol. */
function formatMoney(amount, config) {
  const symbol = (config && config.currency) || '$';
  return `${symbol}${Number(amount).toFixed(2)}`;
}

/**
 * MA-2378: Seal renders the raw internal distributor suffix ("-U" / "-W",
 * MA-1027 — same suffix normalizeName() drops for matching, below) straight
 * into the visible product/variant name. This page is served by the Seal app
 * proxy, not our theme, so the server-rendered markup can't be edited directly
 * — the fix has to run here, after Seal paints each row.
 *
 * Only the name element itself is rewritten (Seal's own quantity/edit controls
 * live in a sibling node under the same wrapper) and the replace is a no-op on
 * text that has already been stripped, so re-running this on every AJAX
 * re-render is safe.
 */
function stripDistributorSuffixes(root) {
  for (const wrapper of root.querySelectorAll(VARIANT_NAME_SELECTOR)) {
    const nameEl = wrapper.firstElementChild || wrapper;
    const stripped = nameEl.textContent.replace(/-[UW]\s*$/, '').trimEnd();
    if (stripped !== nameEl.textContent) nameEl.textContent = stripped;
  }
}

/**
 * Hide Seal's raw internal line-item properties ( strings beginning with "_" ),
 * e.g. "_portal_id: shopmarket" and "_bv: 35.0" — the reward data is re-rendered
 * as pills below.
 */
function hideInternalProps(root) {
  const props = root.querySelectorAll('.seal-line-item-property');
  for (const prop of props) {
    if (prop.textContent.trim().startsWith('_')) {
      prop.classList.add('seal-prop-hidden');
    }
  }
}

/**
 * On the list page, render the designed empty state (Figma: centered
 * subscription icon + message) when there are no subscriptions. Real
 * subscriptions render as clickable `.js-seal-clickable` cards; Seal's own empty
 * state is a single non-clickable `.seal-container` with "You don't have any
 * subscriptions yet." — we replace it with the AC-required copy + icon.
 */
function renderEmptyState(page, config) {
  const list = page.querySelector(LIST_SELECTOR);
  if (!list) return;
  if (list.querySelector('.js-seal-clickable')) return; // has real subscriptions
  if (list.querySelector('.seal-empty-state')) return; // already replaced
  // Only replace Seal's OWN rendered empty message. If the list has no container
  // yet, Seal is still rendering its cards — bail and let the MutationObserver
  // re-run once they appear. This prevents an empty-state flash before the cards
  // load on accounts that DO have subscriptions.
  if (!list.querySelector('.seal-container')) return;
  const message = (config.labels && config.labels.emptyState)
    || 'You currently have no subscriptions to display.';
  const icon = config.emptyIconUrl
    ? `<img class="seal-empty-state__icon" src="${config.emptyIconUrl}" alt="" width="88" height="88" loading="lazy">`
    : '';
  list.innerHTML = `<div class="seal-empty-state">${icon}<p class="seal-empty-state__message">${message}</p></div>`;
}

/**
 * Fetch reward data for a set of product ids via Storefront Search + Section
 * Rendering. Returns a Map<productId(string), productData>.
 */
async function fetchRewards(productIds, signal) {
  const query = productIds.map((id) => `id:${id}`).join(' OR ');
  const url = `${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(query)}&type=product&section_id=${SECTION_ID}`;

  const map = new Map();
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) return map;
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const script = doc.querySelector('[data-variant-rewards]');
    if (!script) return map;
    const parsed = JSON.parse(script.textContent);
    for (const product of parsed.products || []) {
      map.set(String(product.id), product);
    }
  } catch {
    /* graceful degradation — leave map empty */
  }
  return map;
}

/** Pick the variant on a product that best matches a line item's variant name. */
function matchVariant(product, item) {
  const variants = product.variants || [];
  if (variants.length === 0) return null;
  if (variants.length === 1) return variants[0];

  const target = normalizeName(itemVariantName(item));
  let best = null;
  let bestLen = -1;
  for (const variant of variants) {
    const candidates = [variant.name, `${product.title} ${variant.title}`, variant.title];
    for (const candidate of candidates) {
      const norm = normalizeName(candidate);
      if (!norm) continue;
      if ((target.includes(norm) || norm.includes(target)) && norm.length > bestLen) {
        best = variant;
        bestLen = norm.length;
      }
    }
  }
  return best || variants[0];
}

/**
 * Build the reward-pills HTML for a matched variant, applying the SAME
 * show_srp / show_uc / show_bv / show_ibv / show_cashback rules as the rest of
 * the site (snippets/product-rewards.liquid, lines ~334-398). Customer-type
 * customer flags come from the site-wide `ma:customer` meta tag; the
 * variant reward values come from variant-rewards-data.liquid.
 *
 * Summary of the rule set (keep in sync with product-rewards.liquid):
 *   - is_distributor_display = is_ppc AND (UFO|MPA|NM-eligible|LHP) AND the
 *     product has a distributor variant (retail-only products stay retail).
 *   - SRP / UC show only for distributor-display viewers.
 *   - BV shows for UFO / NM-eligible / LHP; IBV only outside US/CA (and not
 *     MPA-eligible) for UFO / LHP; IBV uses ibvOnly for ibv-only customers.
 *   - Cashback and BV/IBV are mutually exclusive: BV/IBV customers never see
 *     Cashback except the Rastelli's US/CA exception; MPA never sees Cashback.
 */
function buildRewardPills(product, variant, linePrice, config) {
  const labels = config.labels || {};
  const customer = getCustomerContext();
  const pills = [];

  const hasDistributorVariant = (product.variants || []).some((v) => v.isDistributorVariant);
  const isDistributorDisplay = customer.isDistributorDisplay && hasDistributorVariant;
  const isRastellis =
    customer.isUsOrCanada && customer.isBvIbvCustomer && product.brand === "Rastelli's";

  const srp = Number(variant.retailSalePrice) || Number(variant.retailOriginalPrice) || 0;
  const uc = Number(variant.distributorSalePrice) || Number(variant.distributorOriginalPrice) || 0;
  const bv = Number(variant.bv) || 0;
  const resolvedIbv = (customer.isIbvOnly ? Number(variant.ibvOnly) : Number(variant.ibv)) || 0;
  const cashbackPct = Math.max(
    Number(variant.standardCashbackPercent) || 0,
    Number(variant.increasedCashbackPercent) || 0
  );

  const showSrp = isDistributorDisplay && srp > 0;
  const showUc = isDistributorDisplay && uc > 0;

  let showCashback = false;
  if (cashbackPct > 0 && linePrice > 0 && !customer.isMpa) {
    if (!customer.isBvIbvCustomer || isRastellis) showCashback = true;
  }

  let showBv = bv > 0 && customer.isBvIbvCustomer;
  let showIbv = customer.canShowIbv && resolvedIbv > 0;
  if (isRastellis) {
    showBv = false;
    showIbv = false;
  }

  if (showSrp) {
    pills.push(
      `<span class="seal-reward-pill seal-reward-pill--srp">${labels.srp || 'SRP'} ${formatMoney(srp, config)}</span>`
    );
  }
  if (showUc) {
    pills.push(
      `<span class="seal-reward-pill seal-reward-pill--uc">${labels.uc || 'UC'} ${formatMoney(uc, config)}</span>`
    );
  }
  if (showBv) {
    pills.push(
      `<span class="seal-reward-pill seal-reward-pill--bv">${labels.bv || 'BV'} ${bv}</span>`
    );
  }
  if (showIbv) {
    pills.push(
      `<span class="seal-reward-pill seal-reward-pill--ibv">${labels.ibv || 'IBV'} ${resolvedIbv}</span>`
    );
  }
  if (showCashback) {
    const amount = formatMoney((linePrice * cashbackPct) / 100, config);
    const icon = config.cashbackIconUrl
      ? `<img src="${config.cashbackIconUrl}" alt="" width="16" height="16" loading="lazy">`
      : '';
    pills.push(
      `<span class="seal-reward-pill seal-reward-pill--cashback">${icon}${labels.cashback || 'Cashback'} ${amount}</span>`
    );
  }

  if (pills.length === 0) return '';
  return `<div class="seal-rewards-row">${pills.join('')}</div>`;
}

/**
 * BCRS deposit note — mirrors the PDP (blocks/product-rewards.liquid, MA-1111):
 * driven entirely by the product metafield `custom.bcrs_deposit_message`, which
 * is only populated where the note applies (Ultimate Aloe Juice on SGP). No
 * country/title/tag guesswork — if the message is present, render it, using the
 * same text as the PDP.
 */
function buildBcrs(product) {
  const message = (product.bcrsMessage || '').trim();
  if (!message) return '';
  const html = message.replace(/\n/g, '<br>');
  return `<p class="seal-bcrs-message">${html}</p>`;
}

/** Inject pills + BCRS into a single line item. */
function injectItem(item, product, config) {
  if (item.querySelector('.seal-rewards-row, .seal-bcrs-message')) return; // no double-inject

  const variant = matchVariant(product, item);
  if (!variant) return;

  const priceEl = item.querySelector('[data-seal-format-money][data-value]');
  const linePrice = priceEl ? parseFloat(priceEl.getAttribute('data-value')) : Number(variant.price) || 0;

  const pillsHtml = buildRewardPills(product, variant, linePrice, config);
  const bcrsHtml = buildBcrs(product);
  if (!pillsHtml && !bcrsHtml) return;

  const target = item.querySelector('.seal-flex-col') || item.querySelector('.seal-item-container') || item;
  target.insertAdjacentHTML('beforeend', pillsHtml + bcrsHtml);
}

/** Augment every subscription line item on the detail page. */
async function augmentItems(page, config, signal) {
  const scope = page.querySelector(ITEMS_LIST_SELECTOR) || page;
  const items = Array.from(scope.querySelectorAll(ITEM_SELECTOR)).filter(
    (item) => !item.dataset.sealRewardsDone
  );
  if (items.length === 0) return;

  const ids = [];
  for (const item of items) {
    const id = productIdFromItem(item);
    if (id && !ids.includes(id)) ids.push(id);
  }
  if (ids.length === 0) return;

  const rewards = await fetchRewards(ids, signal);
  if (signal.aborted) return;

  for (const item of items) {
    item.dataset.sealRewardsDone = '1';
    const id = productIdFromItem(item);
    const product = id && rewards.get(id);
    if (product) injectItem(item, product, config);
  }
}

/** Run the full enhancement pass over the current portal DOM. */
async function enhance(page, config, signal) {
  page.classList.add(getCustomerContext().isPpc ? 'seal-portal--ppc' : 'seal-portal--pc');
  configureQuantityInputs(page);
  hideInternalProps(page);
  stripDistributorSuffixes(page);
  // MA-3046: disabled. renderEmptyState() infers "no subscriptions" from the
  // absence of `.js-seal-clickable` cards and then overwrites the list, which
  // can hide real subscriptions if Seal renders a card without that class or
  // adds it after our first pass. Seal's own empty message is shown instead
  // until the list markup is confirmed.
  // renderEmptyState(page, config);
  await augmentItems(page, config, signal);
}

function init() {
  const config = getConfig();
  const page = document.querySelector(PAGE_SELECTOR);
  if (!config || !page) return;

  const controller = new AbortController();
  bindQuantityInputGuards(page);
  let pending = false;
  const run = () => {
    if (pending) return;
    pending = true;
    // Allow Seal's own scripts (time conversion, select upgrades) to settle.
    setTimeout(() => {
      pending = false;
      enhance(page, config, controller.signal);
    }, 150);
  };

  run();

  // Seal re-renders the items list via AJAX after edit/save — re-augment then.
  const target = page.querySelector(ITEMS_LIST_SELECTOR) || page;
  const observer = new MutationObserver(run);
  observer.observe(target, { childList: true, subtree: true });

  // Best-effort: also respond to Seal SDK "loaded" events if they fire.
  document.addEventListener('seal_loaded', run);
  document.addEventListener('seal:portal:loaded', run);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
